import { chains,ZERO } from '@emberswap/emberswap-sdk2/dist/constants'
import { Amount, Fraction, Native } from '@emberswap/emberswap-sdk2/dist/entities'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import React, { CSSProperties, FC, memo, useCallback, useRef } from 'react'

import { classNames } from '../../functions'
import { useInViewport } from '../../hooks'
import { Badge } from '../Badge'
import { Currency } from '../Currency'
import { CheckCircleIcon, Close, ExternalLinkIcon, NetworkIcon, XCircleIcon } from '../Icons'
import { IconButton } from '../Icons'

export interface TokenSelectorRow {
  account?: `0x${string}`
  currency: Type
  style?: CSSProperties
  className?: string
  onSelect(currency: Type): void
  balance?: Amount<Type> | undefined
  price?: Fraction
  disableNative?: boolean
  pin?: {
    isPinned: boolean
    onPin(): void
  }
  onPath?: {
    onRemovePath: () => void;
    isPath: boolean;
} | undefined
  midSelector?: boolean
  selected: boolean
}

export const TokenSelectorRow: FC<TokenSelectorRow> = memo(function TokenSelectorRow({
  price,
  balance,
  currency,
  style,
  disableNative = false,
  className = '',
  onSelect,
  pin,
  onPath,
  midSelector,
  selected,
}) {
  const onClick = useCallback(() => {
    onSelect(currency)
  }, [currency, onSelect])

  const onPin = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation()
      pin?.onPin()
    },
    [pin]
  )
  const ref = useRef<HTMLDivElement>(null)
  const inViewport = useInViewport(ref)
  return (
    <div
      onClick={onClick}
      className={classNames(
        className,
        `group flex items-center  hover:bg-blue-600 focus:bg-red-500 h-[48px] token-${currency?.symbol}`
      )}
      style={style}
    >
      <div className="flex items-center justify-between flex-grow gap-2 rounded cursor-pointer">
        <div className="flex flex-row items-center flex-grow gap-2">
          {selected ? (
            <Badge
              // position="bottom-right"
              badgeContent={
                <div className="rounded-full bg-slate-300">
                  <CheckCircleIcon width={14} height={14} className="rounded-full fill-fire1" />
                </div>
              }
            >
              <div className="w-7 h-7">
                <Currency.Icon
                  disableLink
                  currency={currency}
                  width={28}
                  height={28}
                  priority={inViewport}
                />
              </div>
            </Badge>
          ) : (
            <div className="h-7 w-7">
              <Currency.Icon
                disableLink
                currency={currency}
                width={28}
                height={28}
                priority={inViewport}
              />
            </div>
          )}
          <div className="flex flex-col items-start">
            <a className="text-xs font-medium text-primary group-hover:text-slate-50">
              {disableNative && currency.symbol === Native.onChain(currency.chainId).wrapped.symbol
                ? Native.onChain(currency.chainId).symbol
                : currency.symbol}
            </a>
            <a className="text-xs text-slate-500 group-hover:text-blue-100">
              {disableNative && currency.name === Native.onChain(currency.chainId).wrapped.name
                ? Native.onChain(currency.chainId).name
                : currency.name}
            </a>
          </div>
        </div>

        {balance?.greaterThan(ZERO) && !midSelector  && (
          <div className="flex flex-col">
            <a className="text-xs font-medium text-right text-primary">
              {balance.toSignificant(6)}
            </a>
            <a className="text-right text-xxs text-slate-400">
              {price ? `$${balance.multiply(price).toFixed(2)}` : '-'}
            </a>
          </div>
        )}
        {onPath && onPath.isPath && midSelector && (
          <IconButton
            name="pin"
            onClick={onPath.onRemovePath}
            className="z-50 mr-2 opacity-50 grayscale"
          >
            <Close className="w-6 h-6 fill-slate-500" />
          </IconButton>
        )}
        {pin && (
          <IconButton
            name="pin"
            onClick={onPin}
            className={classNames(pin.isPinned ? '' : 'grayscale opacity-50', 'z-50')}
          >
            ⭐
          </IconButton>
        )}
      </div>
    </div>
  )
})


export const TokenSelectorCustomTokenRow: FC<{
  style: CSSProperties
  currency: Type
  onRemove(): void
}> = ({ style, currency, onRemove }) => {
  return (
    <div className="flex items-center w-full p-4" style={style}>
      <div className="flex items-center justify-between flex-grow gap-2 rounded cursor-pointer">
        <div className="flex flex-row items-center flex-grow gap-2">
          <div className="w-7 h-7">
            <Currency.Icon disableLink currency={currency} width={28} height={28} />
          </div>
          <div className="flex flex-col items-start">
            <a className="text-xs font-medium text-slate-200">{currency.symbol}</a>
            <div className="flex gap-1">
              <NetworkIcon type="naked" chainId={currency.chainId} width={14} height={14} />
              <a className="text-xxs text-slate-500">{chains[currency.chainId].name}</a>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <IconButton onClick={onRemove}>
            <XCircleIcon width={20} height={20} className="text-slate-500" />
          </IconButton>
          <IconButton
            as="a"
            rel="noopener noreffer"
            target="_blank"
            href={chains[currency.chainId].getTokenUrl(currency.wrapped.address)}
          >
            <ExternalLinkIcon width={20} height={20} className=" fill-blue-500" />
          </IconButton>
        </div>
      </div>
    </div>
  )
}